// @flow
/** @jsx jsx */

import { marginToCss } from '@zapier/common-utils';
import {
  PropsQueriesType,
  PropsQuery,
} from '../PropsQuery';
import { css, jsx } from '@emotion/core';

import type { Node } from 'react';
import type { Spacing } from '@zapier/common-utils';

type Props = {
  /** Flex alignItems value */
  alignItems:
    | 'center'
    | 'flex-end'
    | 'flex-start'
    | 'stretch'
    | 'baseline'
    | 'inherit',
  children: Node,
  /** Flex direction */
  direction: 'column' | 'row',
  /** Join buttons with no margin */
  isConjoined: boolean,
  /** Apply flex to buttons */
  isFlex: boolean,
  /** Reverse flex direction */
  isReversed: boolean,
  /** Flex justify-content value */
  justify:
    | 'flex-start'
    | 'flex-end'
    | 'start'
    | 'end'
    | 'left'
    | 'right'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly',
  /** Custom margin (spacing utility) */
  margin?: Spacing,
  /** Props Queries */
  propsQueries?: PropsQueriesType,
};

const buttonGroupCss = ({
  alignItems,
  direction,
  isConjoined,
  isFlex,
  isReversed,
  justify,
  margin,
}: Props) =>
  css(
    {
      alignItems,
      display: 'flex',
      flexDirection: `${direction}${isReversed ? '-reverse' : ''}`,
      justifyContent: justify,
      '> *': {
        display: 'block',
        flex: isFlex ? 1 : 'none',
        width: direction === 'row' ? 'auto' : '100%',
      },
      ...marginToCss(margin),
    },
    isConjoined
      ? {
          '> *:not(:first-child)': {
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
            borderLeftWidth: 0,
          },
          '> *:not(:last-child)': {
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
          },
        }
      : {
          [`> *:not(:${isReversed ? 'first' : 'last'}-child)`]: {
            margin: direction === 'column' ? '0 0 1em' : '0 1em 0 0',
          },
        }
  );

const ButtonGroup = (props: Props) => {
  return (
    <PropsQuery queries={props.propsQueries}>
      {overrides => (
        <div css={buttonGroupCss({ ...props, ...overrides })}>
          {props.children}
        </div>
      )}
    </PropsQuery>
  );
};

ButtonGroup.defaultProps = {
  direction: 'column',
  isConjoined: false,
  isFlex: false,
  isReversed: false,
  justify: 'flex-start',
  alignItems: 'inherit',
};

export default ButtonGroup;
